import React, { useState, useEffect, useMemo } from 'react';
import { FaEye } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import ShoppingModal from '../components/ShoppingModal';
import { useEnhancedFetch } from '../hooks/useRequestManager';
import Loader from '../components/Loader';
import { toast } from 'react-toastify';
import Pagination from '../components/Pagination';
import { useAuth } from '../contexts/AuthContext';
import { Modal, Button, Nav, Tab } from 'react-bootstrap';
import Select from 'react-select';
import InvoiceDetailsModal from '../components/InvoiceDetailsModal';

const Xaridlar = () => {
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const { token, user } = useAuth();

  // Filters
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierId, setSupplierId] = useState('');

  // Set default date range to current date
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState(getCurrentDate());
  const [endDate, setEndDate] = useState(getCurrentDate());

  // Details modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchProducts, setBatchProducts] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);

  // Barcodes modal state
  const [showBarcodesModal, setShowBarcodesModal] = useState(false);
  const [barcodes, setBarcodes] = useState([]);
  const [barcodesLoading, setBarcodesLoading] = useState(false);
  const [barcodesError, setBarcodesError] = useState(null);

  // Build query parameters for backend filtering
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.set('page', currentPage.toString());
    params.set('limit', itemsPerPage.toString());
    if (supplierId) params.set('supplierId', supplierId);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return params.toString();
  };

  const { data: batchesData, loading, error, refetch } = useEnhancedFetch(
    `/api/purchases/batches?${buildQueryParams()}`,
    {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'x-owner-id': user?.ownerId || user?.id || '',
        'x-staff-id': user?.staffId || ''
      }
    }
  );

  // Invoices Fetch
  const [activeTab, setActiveTab] = useState('invoices');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  const { data: invoicesData, loading: invoicesLoading, error: invoicesError, refetch: refetchInvoices } = useEnhancedFetch(
    `/api/invoices?${buildQueryParams()}`,
    {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'x-owner-id': user?.ownerId || user?.id || '',
        'x-staff-id': user?.staffId || ''
      }
    }
  );

  // Refresh both on modal close
  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    refetch();
    refetchInvoices();
  };

  useEffect(() => {
    if (error) {
      toast.error('Xaridlarni yuklashda xatolik: ' + error);
    }
  }, [error]);

  // Load suppliers for filter
  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const res = await fetch('/api/suppliers', {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'x-owner-id': user?.ownerId || user?.id || '',
            'x-staff-id': user?.staffId || ''
          }
        });
        if (!res.ok) throw new Error('Ta\'minlovchilarni yuklashda xatolik');
        const data = await res.json();
        const opts = (Array.isArray(data) ? data : []).map(s => ({ value: s.supplierid, label: s.name }));
        setSupplierOptions(opts);
      } catch (e) {
        toast.error(e.message);
      }
    };
    loadSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCurrency = (amount, currency) => {
    if (amount == null) return '-';
    return `${Number(amount).toLocaleString()} ${currency || ''}`.trim();
  };

  // Format date as: DD-MM-YY , HH-MM
  const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '-';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const HH = String(d.getHours()).padStart(2, '0');
    const MM = String(d.getMinutes()).padStart(2, '0');
    return `${dd}-${mm}-${yy} , ${HH}:${MM}`;
  };

  const openDetails = async (batch) => {
    setSelectedBatch(batch);
    setShowDetailsModal(true);
    setDetailsLoading(true);
    setDetailsError(null);
    setBatchProducts([]);
    try {
      const res = await fetch(`/api/purchases/batch/${batch.batchid}/products`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'x-owner-id': user?.ownerId || user?.id || '',
          'x-staff-id': user?.staffId || ''
        }
      });
      if (!res.ok) throw new Error('Server error');
      const data = await res.json();
      setBatchProducts(Array.isArray(data) ? data : []);
    } catch (e) {
      setDetailsError(e.message);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Group products by product for details modal (specific method shows one row per barcode)
  const groupedProducts = useMemo(() => {
    if (!Array.isArray(batchProducts) || batchProducts.length === 0) return [];
    const byProduct = new Map();
    batchProducts.forEach(item => {
      const key = String(item.productid);
      const originalStore = item.purchase_store_name || item.original_store_name || item.initial_store_name || item.store_name;
      if (!byProduct.has(key)) {
        byProduct.set(key, {
          productid: item.productid,
          product_name: item.product_name,
          total_quantity: Number(item.quantity_purchased) || 0,
          costperunit_values: new Set(item.costperunit != null ? [String(item.costperunit)] : []),
          first_price_values: new Set(item.first_price != null ? [String(item.first_price)] : []),
          sellingprice_values: new Set(item.sellingprice != null ? [String(item.sellingprice)] : []),
          currency_values: new Set(item.currency ? [item.currency] : []),
          original_store_names: new Set(originalStore ? [originalStore] : []),
          barcodes: item.barcode ? [item.barcode] : []
        });
      } else {
        const agg = byProduct.get(key);
        agg.total_quantity += Number(item.quantity_purchased) || 0;
        if (item.costperunit != null) agg.costperunit_values.add(String(item.costperunit));
        if (item.first_price != null) agg.first_price_values.add(String(item.first_price));
        if (item.sellingprice != null) agg.sellingprice_values.add(String(item.sellingprice));
        if (item.currency) agg.currency_values.add(item.currency);
        const originalStore2 = item.purchase_store_name || item.original_store_name || item.initial_store_name || item.store_name;
        if (originalStore2) agg.original_store_names.add(originalStore2);
        if (item.barcode) agg.barcodes.push(item.barcode);
      }
    });
    const toValueOrDash = (set) => set.size === 1 ? Array.from(set)[0] : (set.size > 1 ? Array.from(set)[0] : '—');
    return Array.from(byProduct.values()).map(v => ({
      productid: v.productid,
      product_name: v.product_name,
      quantity_purchased: v.total_quantity,
      costperunit: toValueOrDash(v.costperunit_values),
      first_price: toValueOrDash(v.first_price_values),
      sellingprice: toValueOrDash(v.sellingprice_values),
      currency: toValueOrDash(v.currency_values),
      store_name: toValueOrDash(v.original_store_names),
      barcodes: v.barcodes
    }));
  }, [batchProducts]);

  const closeDetails = () => {
    setShowDetailsModal(false);
    setSelectedBatch(null);
    setBatchProducts([]);
    setDetailsError(null);
  };

  const openBarcodes = (productId) => {
    if (!selectedBatch) return;
    setBarcodesError(null);
    // Gather all barcodes for this product within the selected batch (Specific method rows only)
    const codes = (batchProducts || [])
      .filter(it => String(it.productid) === String(productId) && it.barcode)
      .map(it => it.barcode);
    setBarcodes(codes);
    setBarcodesLoading(false);
    setShowBarcodesModal(true);
  };

  const closeBarcodes = () => {
    setShowBarcodesModal(false);
    setBarcodes([]);
    setBarcodesError(null);
  };

  // Totals by currency for filtered range & supplier
  const [totalsLoading, setTotalsLoading] = useState(false);
  const [totalsError, setTotalsError] = useState(null);
  const [totalsByCurrency, setTotalsByCurrency] = useState({});

  useEffect(() => {
    const computeTotals = async () => {
      try {
        setTotalsLoading(true);
        setTotalsError(null);
        setTotalsByCurrency({});

        // Fetch all batches for the range respecting filters (backend-side filtering)
        const query = new URLSearchParams();
        query.set('page', '1');
        query.set('limit', '100000'); // Get all for totals calculation
        if (supplierId) query.set('supplierId', supplierId);
        if (startDate) query.set('startDate', startDate);
        if (endDate) query.set('endDate', endDate);

        const allBatchesRes = await fetch(`/api/purchases/batches?${query.toString()}`,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'x-owner-id': user?.ownerId || user?.id || '',
              'x-staff-id': user?.staffId || ''
            }
          }
        );
        if (!allBatchesRes.ok) throw new Error('Agregatsiyani yuklashda xatolik');
        const allBatchesData = await allBatchesRes.json();
        const allBatches = Array.isArray(allBatchesData.items) ? allBatchesData.items : (Array.isArray(allBatchesData) ? allBatchesData : []);

        const headers = {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'x-owner-id': user?.ownerId || user?.id || '',
          'x-staff-id': user?.staffId || ''
        };

        const productsPerBatch = await Promise.all(
          allBatches.map(async (b) => {
            try {
              const r = await fetch(`/api/purchases/batch/${b.batchid}/products`, { headers });
              if (!r.ok) return [];
              const data = await r.json();
              return Array.isArray(data) ? data : [];
            } catch {
              return [];
            }
          })
        );

        const totals = {};
        for (const arr of productsPerBatch) {
          for (const it of arr) {
            const qtyPurchased = Number(it.quantity_purchased) || 0;
            const cost = Number(it.costperunit) || 0;
            if (qtyPurchased > 0 && cost > 0) {
              const currency = it.currency || 'UZS';
              totals[currency] = (totals[currency] || 0) + (qtyPurchased * cost);
            }
          }
        }

        setTotalsByCurrency(totals);
      } catch (e) {
        setTotalsError(e.message || 'Xatolik yuz berdi');
      } finally {
        setTotalsLoading(false);
      }
    };

    computeTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId, startDate, endDate, token, user]);

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Xaridlar</h2>
        <button className="btn btn-success" onClick={handleShowModal}>Qo'shish</button>
      </div>

      {/* Filters */}
      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <Select
            classNamePrefix="select"
            options={supplierOptions}
            value={supplierOptions.find(o => String(o.value) === String(supplierId)) || null}
            onChange={(opt) => { setSupplierId(opt ? opt.value : ''); setCurrentPage(1); }}
            isClearable
            placeholder="Ta'minlovchini tanlang"
          />
        </div>
        <div className="col-md-3">
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
          />
        </div>
        <div className="col-md-3">
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
          />
        </div>
        <div className="col-md-2 d-grid">
          <button
            className="btn btn-outline-secondary"
            onClick={() => { setSupplierId(''); setStartDate(getCurrentDate()); setEndDate(getCurrentDate()); setCurrentPage(1); }}
          >
            Tozalash
          </button>
        </div>
      </div>


      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="invoices">Invoyslar</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="history">Xaridlar Tarixi</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="invoices">
            {invoicesLoading ? <Loader /> : invoicesError ? <div className="alert alert-danger">{invoicesError}</div> : (
              <>
                <div className="table-responsive rounded-4 shadow">
                  <table className="table table-striped mb-0">
                    <thead>
                      <tr>
                        <th>Nº</th>
                        <th>Ta'minlovchi</th>
                        <th>Jami narx</th>
                        <th>Valyuta</th>
                        <th>Bajaruvchi</th>
                        <th>Sana</th>
                        <th>Holat</th>
                        <th>Amallar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(invoicesData?.items || []).map((inv, idx) => (
                        <tr key={inv.batchid}>
                          <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                          <td>{inv.supplier_name}</td>
                          <td>{Number(inv.total_price).toLocaleString()}</td>
                          <td>{inv.supplier_currency}</td>
                          <td>{inv.staff_full_name || inv.owner_business_name}</td>
                          <td>{formatDateTime(inv.created_at)}</td>
                          <td>
                            {inv.invoice_status === 0 ? <span className="badge bg-warning">Yangi</span> :
                              inv.invoice_status === 1 ? <span className="badge bg-success">Tasdiqlangan</span> :
                                <span className="badge bg-danger">Rad etilgan</span>}
                          </td>
                          <td>
                            <button className="btn btn-sm btn-outline-primary" onClick={() => { setSelectedInvoice(inv); setShowInvoiceModal(true); }}>
                              <FaEye /> Tafsilotlar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalItems={invoicesData?.total || 0}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(v) => { setItemsPerPage(v); setCurrentPage(1); }}
                />
              </>
            )}
          </Tab.Pane>
          <Tab.Pane eventKey="history">
            {loading ? (
              <Loader />
            ) : error ? (
              <div className="alert alert-danger text-center">Xaridlarni yuklashda xatolik yuz berdi.</div>
            ) : !batchesData || !batchesData.items || batchesData.items.length === 0 ? (
              <div className="alert alert-info text-center">Xaridlar topilmadi.</div>
            ) : (
              <>
                {/* Totals by currency */}
                <div className="row g-2 mb-3">
                  <div className="col-12">
                    <div className="card shadow-sm">
                      <div className="card-body">
                        <h6 className="card-title mb-2">Tanlangan davr bo'yicha jami xaridlar</h6>
                        {totalsLoading ? (
                          <div className="text-muted">Hisoblanmoqda...</div>
                        ) : totalsError ? (
                          <div className="text-danger">{totalsError}</div>
                        ) : Object.keys(totalsByCurrency).length === 0 ? (
                          <div className="text-muted">Ma'lumot topilmadi</div>
                        ) : (
                          <div className="d-flex flex-wrap gap-2">
                            {Object.entries(totalsByCurrency).map(([cur, sum]) => (
                              <span key={cur} className="badge text-bg-primary fs-6">
                                {Number(sum).toLocaleString()} {cur}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="table-responsive rounded-4 shadow">
                  <table className="table table-striped mb-0">
                    <thead>
                      <tr>
                        <th>Nº</th>
                        <th>Ta'minlovchi</th>
                        <th>Jami narx</th>
                        <th>Valyuta</th>
                        <th>Bajaruvchi</th>
                        <th>Sana</th>
                        <th>Tafsilotlar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batchesData.items.map((batch, index) => (
                        <tr key={`${batch.batchid}-${index}`}>
                          <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                          <td>{batch.supplier_name}</td>
                          <td>{batch.total_price ? Number(batch.total_price).toLocaleString() : 0}</td>
                          <td>{batch.supplier_currency || 'UZS'}</td>
                          <td>{batch.staff_id ? (batch.staff_full_name || '—') : (batch.owner_business_name || '—')}</td>
                          <td>{formatDateTime(batch.purchase_date)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openDetails(batch)}
                              title="Ko'rish"
                            >
                              <FaEye /> Ko'rish
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalItems={batchesData.total || 0}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(v) => { setItemsPerPage(v); setCurrentPage(1); }}
                />
              </>
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={closeDetails} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Partiya tafsilotlari (ID: {selectedBatch?.batchid})</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailsLoading ? (
            <div className="text-center py-4">Yuklanmoqda...</div>
          ) : detailsError ? (
            <div className="text-center text-danger py-4">Xatolik: {detailsError}</div>
          ) : batchProducts.length === 0 ? (
            <div className="text-center py-4">Hech qanday mahsulot topilmadi</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Mahsulot</th>
                    <th>Soni</th>
                    <th>Xarid narxi</th>
                    <th>Birinchi narx</th>
                    <th>Sotish narxi</th>
                    <th>Valyuta</th>
                    <th>Ombor</th>
                    <th>Shtrix-kodlar</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedProducts.map((item, idx) => (
                    <tr key={item.productid || idx}>
                      <td>{item.product_name || '-'}</td>
                      <td>{Number(item.quantity_purchased) || 0}</td>
                      <td>{formatCurrency(item.costperunit === '—' ? null : item.costperunit, item.currency)}</td>
                      <td>{formatCurrency(item.first_price === '—' ? null : item.first_price, item.currency)}</td>
                      <td>{formatCurrency(item.sellingprice === '—' ? null : item.sellingprice, item.currency)}</td>
                      <td>{item.currency || '-'}</td>
                      <td>{item.store_name || '-'}</td>
                      <td>
                        {item.barcodes && item.barcodes.length > 0 ? (
                          <Button variant="outline-secondary" size="sm" onClick={() => openBarcodes(item.productid)}>
                            Shtrix-kodlar
                          </Button>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="d-flex justify-content-start align-items-center mt-3">
                <div className="text-muted small">
                  Bajaruvchi: {selectedBatch?.staff_id ? (selectedBatch?.staff_full_name || '—') : (selectedBatch?.owner_business_name || '—')}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Barcodes Modal */}
      <Modal show={showBarcodesModal} onHide={closeBarcodes} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>Shtrix-kodlar (Batch: {selectedBatch?.batchid})</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {barcodesLoading ? (
            <div className="text-center py-4">Yuklanmoqda...</div>
          ) : barcodesError ? (
            <div className="text-center text-danger py-4">Xatolik: {barcodesError}</div>
          ) : (barcodes.length === 0 ? (
            <div className="text-center py-4">Shtrix-kodlar topilmadi</div>
          ) : (
            <ul className="list-group">
              {barcodes.map((bc, i) => (
                <li key={i} className="list-group-item d-flex justify-content-between align-items-center">
                  <span>{bc}</span>
                </li>
              ))}
            </ul>
          ))}
        </Modal.Body>
      </Modal>

      {/* Create Purchase Modal */}
      <ShoppingModal show={showModal} handleClose={handleCloseModal} />

      <InvoiceDetailsModal
        show={showInvoiceModal}
        handleClose={() => { setShowInvoiceModal(false); setSelectedInvoice(null); }}
        invoiceBatch={selectedInvoice}
        refreshData={() => { refetch(); refetchInvoices(); }}
      />
    </div>
  );
};

export default Xaridlar;
